import type { ReactElement } from 'react';
import { CloudDownload } from 'lucide-react';
import type { AgentUpdateState } from '@kirin/api-client/contracts/agent-auto-update';
import { AgentVersionBadge } from './AgentVersionBadge';
import { AgentUpdateStateBadge } from './AgentUpdateStateBadge';
import { isAgentVersionOutdated } from './agentVersion';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface AgentVersionWithUpdateProps {
  version: string | null | undefined;
  /**
   * The real latest GA version (from `useLatestReleases` → `/api/releases/latest`).
   * Freshness is judged against THIS, not the dashboard build's
   * `VITE_VSIX_VERSION`, so a version ahead of GA isn't wrongly flagged.
   */
  latestVersion: string | null | undefined;
  /**
   * KIR-5191: the automatic-update state. Omit (or pass `null`) where the
   * `agent_auto_update` flag is off or the state is unknown: the badge then
   * behaves exactly as before.
   */
  updateState?: AgentUpdateState | null;
  className?: string;
}

/**
 * KIR-2962: an agent version plus an amber "Update available" pill when it's
 * older than the real latest release. Shared by the Inventory → Users DataTable
 * cell and the endpoints dialog so both judge freshness against the same GA
 * source and render the badge identically. Never nags on uncertainty (unknown
 * version or unknown GA → no pill).
 */
export function AgentVersionWithUpdate({
  version,
  latestVersion,
  updateState = null,
  className,
}: AgentVersionWithUpdateProps): ReactElement {
  const outdated = isAgentVersionOutdated(version, latestVersion);
  // KIR-5191: an "Up to date" state older than the latest release only means
  // the agent has not checked since that release — show "Update available"
  // alone. While updating, failed, held or pinned, the state says more than
  // the pill. With updates Off, or on a machine that cannot update by itself
  // (no helper), both apply: someone has to update it by hand.
  const showUpdateAvailable =
    outdated &&
    (updateState === null ||
      updateState === 'up_to_date' ||
      updateState === 'off' ||
      updateState === 'not_available');
  const showState =
    updateState !== null && !(updateState === 'up_to_date' && outdated);
  return (
    <span className="inline-flex items-center gap-1.5">
      <AgentVersionBadge
        version={version}
        currentVersion={latestVersion}
        className={className}
      />
      {showUpdateAvailable && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              <CloudDownload className="h-3 w-3" />
              Update available
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-60">
            A newer Kirin release is available — the latest is v{latestVersion},
            this is on v{version}.
          </TooltipContent>
        </Tooltip>
      )}
      {showState && <AgentUpdateStateBadge state={updateState} />}
    </span>
  );
}

AgentVersionWithUpdate.displayName = 'AgentVersionWithUpdate';
